/**
 * O SERVIDOR DE VERDADE — a implementação Supabase do contrato `Servidor`.
 *
 * `sincronismo.ts` fala com uma interface de dois métodos, `subir` e `baixar`,
 * e nunca soube o que há do outro lado. É por isso que os vinte testes de
 * offline rodam contra um servidor de mentira, com falha na 37ª de 100 e ordem
 * preservada, sem subir nada. Este arquivo é o outro lado.
 *
 * ⚠️ NÃO USA O CLIENTE `@supabase/supabase-js`. Usa `fetch` contra PostgREST
 * direto, por três razões:
 *
 *   1. O módulo não tem dependência de runtime nenhuma hoje — nem React, nem
 *      cliente de banco. O tablet baixa 190 kB. Trazer o SDK por duas chamadas
 *      HTTP dobraria isso, num aparelho que precisa abrir rápido em rede de
 *      galpão.
 *   2. As duas chamadas que interessam são RPC. O SDK não facilita nada aqui.
 *   3. Sem SDK, o mesmo arquivo roda no navegador, no Node do agente e no
 *      teste, sem adaptador.
 *
 * ⚠️ TODA ESCRITA PASSA POR RPC, nunca por tabela. Não é estilo: a migration
 * 0006 tirou `insert`, `update` e `delete` de `authenticated` em todas as
 * tabelas. Se este arquivo tentasse escrever direto, levaria 403 — e é essa a
 * intenção. As funções validam antes de gravar; a tabela não valida nada.
 */

import type { NomeColecao } from '@pcpgbu/dados';
import type { ItemDaFila, PaginaDeDescida, RespostaDeSubida, Servidor } from './index.js';

export interface OpcoesDoServidorSupabase {
  /** `https://api.centriu.com` em produção. */
  url: string;
  /** A chave anônima. O isolamento de verdade é o RLS, não ela. */
  chaveAnonima: string;
  /** O token do usuário logado; sem ele o RLS não deixa ler nada. */
  token: () => string | null;
  /** O aparelho, para o servidor saber qual setor mandar. */
  dispositivoId?: string | null;
  /**
   * Qual tabela/visão de OS baixar.
   *
   * O tablet usa a visão `pcpgbu_os_producao` (sem contato do cliente, RN-052); o
   * escritório usa a tabela cheia `pcpgbu_os`, que traz o contato e as
   * referências de arquivo (`pdf_ref`, `imagem_faca_ref`).
   */
  tabelaOs?: string;
  buscar?: typeof fetch;
}

/**
 * De coleção local para tabela do servidor.
 *
 * ⚠️ AS DUAS PONTAS USAM O MESMO `id`. É o que a semente derivada por UUID v5
 * garante: `est-guilhotina` vira o mesmo uuid nos dois lados. Sem isso, cada
 * descida precisaria de uma tabela de tradução — e tradução de identidade é
 * onde os sistemas de sincronismo apodrecem.
 */
const TABELA: Partial<Record<NomeColecao, string>> = {
  setor: 'pcpgbu_setor',
  estacao: 'pcpgbu_estacao',
  dePara: 'pcpgbu_de_para_estacao',
  motivo: 'pcpgbu_motivo_pausa',
  pessoa: 'pcpgbu_pessoa_publica',
  cliente: 'pcpgbu_cliente',
  os: 'pcpgbu_os_producao',
  osPlano: 'pcpgbu_os_plano',
  osEtapa: 'pcpgbu_os_etapa',
  osMaterial: 'pcpgbu_os_material',
  evento: 'pcpgbu_evento_producao',
};

/**
 * A coluna que serve de marca d'água em cada tabela.
 *
 * ⚠️ EVENTO USA `created_at`, E NÃO `updated_at`, porque evento não se
 * atualiza — o gatilho `pcpgbu_evento_sem_update` recusa. Se a descida
 * ordenasse por `updated_at` num lugar onde ela nunca muda, a marca d'água
 * ficaria parada e o tablet baixaria os mesmos eventos para sempre.
 */
const COLUNA_MARCA: Partial<Record<NomeColecao, string>> = { evento: 'created_at' };

/**
 * Coleções-filhas que referenciam a OS por `os_id`, e não pelo número.
 *
 * ⚠️ O app chaveia tudo por `osNumero` (a chave natural da OS), mas o banco
 * referencia por `os_id` (uuid). Sem esta tradução, o registro chega com
 * `osId` e sem `osNumero`: a etapa existe no depósito, mas nenhuma OS a
 * encontra — e o quadro, a fila e o detalhe aparecem vazios "com o banco
 * cheio". O PostgREST embute a OS pela chave estrangeira e o número vem junto.
 */
const EMBUTE_NUMERO_DA_OS: Partial<Record<NomeColecao, boolean>> = {
  osPlano: true,
  osEtapa: true,
  osMaterial: true,
};

/**
 * `tipo_linha` → `tipoLinha`.
 *
 * ⚠️ O POSTGREST FALA snake_case E O BANCO LOCAL FALA camelCase, e a tradução
 * tem de acontecer AQUI, na fronteira. Sem ela a descida "funciona": as linhas
 * chegam, são gravadas, e a contagem bate. Só que `etapa.tipoLinha` fica
 * `undefined`, `etapa.osId` fica `undefined`, e a fila da estação aparece
 * VAZIA — com o banco cheio. Foi exatamente assim que apareceu: 10 etapas
 * baixadas e 0 etapas de operação encontradas.
 *
 * A conversão é genérica de propósito: uma lista de-para campo a campo seria
 * mais uma coisa para esquecer de atualizar quando a tabela ganhar coluna.
 */
function paraCamel<T>(linha: Record<string, unknown>): T {
  const saida: Record<string, unknown> = {};
  for (const [chave, valor] of Object.entries(linha)) {
    saida[chave.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())] = valor;
  }
  return saida as T;
}

/**
 * Troca a OS embutida (`os: { numero }`) pelo `osNumero` que o app usa.
 * Coleção sem embed passa direto. Ver `EMBUTE_NUMERO_DA_OS`.
 */
function desembutirNumeroDaOs(
  linha: Record<string, unknown>,
  embute: boolean,
): Record<string, unknown> {
  if (!embute) return linha;
  const os = linha['os'] as { numero?: number } | undefined;
  linha['osNumero'] = os?.numero ?? null;
  delete linha['os'];
  return linha;
}

export class ServidorSupabase implements Servidor {
  private readonly buscar: typeof fetch;

  constructor(private readonly o: OpcoesDoServidorSupabase) {
    this.buscar = o.buscar ?? globalThis.fetch.bind(globalThis);
  }

  private cabecalhos(): Record<string, string> {
    const t = this.o.token();
    return {
      apikey: this.o.chaveAnonima,
      Authorization: `Bearer ${t ?? this.o.chaveAnonima}`,
      'Content-Type': 'application/json',
    };
  }

  private async rpc(nome: string, corpo: unknown): Promise<unknown> {
    const r = await this.buscar(`${this.o.url}/rest/v1/rpc/${nome}`, {
      method: 'POST',
      headers: this.cabecalhos(),
      body: JSON.stringify(corpo),
    });
    const texto = await r.text();
    if (!r.ok) throw new Error(`${nome}: HTTP ${r.status} ${texto.slice(0, 300)}`);
    return texto ? (JSON.parse(texto) as unknown) : null;
  }

  /** O nome da organização atual (a empresa que veste este PCP). */
  async orgNome(): Promise<string | null> {
    const r = await this.rpc('pcpgbu_org_nome', {});
    return typeof r === 'string' && r.trim() ? r : null;
  }

  /**
   * Sobe o que está na fila local.
   *
   * ⚠️ O LOTE INTEIRO VAI NUMA CHAMADA SÓ, para `pcpgbu_eventos_registrar_lote`.
   * Não é otimização: é a diferença entre um tablet que volta de duas horas
   * offline com 80 apontamentos fazendo 80 requisições numa rede ruim, e um
   * que faz uma. E como a chave de cada evento nasceu no aparelho, reenviar o
   * lote é inofensivo — o servidor reconhece o que já tem.
   */
  async subir(itens: readonly ItemDaFila[]): Promise<RespostaDeSubida[]> {
    if (itens.length === 0) return [];

    const eventos = itens.filter((i) => i.tipo === 'evento');
    const resto = itens.filter((i) => i.tipo !== 'evento');
    const respostas: RespostaDeSubida[] = [];

    if (eventos.length > 0) {
      const r = (await this.rpc('pcpgbu_eventos_registrar_lote', {
        p_eventos: eventos.map((e) => e.carga),
      })) as Array<{ id?: string; resultado?: string; mensagem?: string }> | null;

      const porId = new Map((r ?? []).map((x) => [String(x.id), x]));
      for (const e of eventos) {
        const achou = porId.get(e.id);
        respostas.push({
          id: e.id,
          resultado: (achou?.resultado as RespostaDeSubida['resultado']) ?? 'aceito',
          ...(achou?.mensagem ? { mensagem: achou.mensagem } : {}),
        });
      }
    }

    /* O que não é evento vai um a um: são ações raras do encarregado
       (prioridade, cancelamento, expedição) e cada uma tem a sua função. */
    for (const item of resto) {
      try {
        await this.rpc(FUNCAO_POR_TIPO[item.tipo] ?? item.tipo, item.carga);
        respostas.push({ id: item.id, resultado: 'aceito' });
      } catch (erro) {
        /* ⚠️ RECUSADO ≠ FALHOU. 4xx quer dizer que o servidor entendeu e disse
           não — reenviar não muda nada, e o item vai para a fila morta com a
           mensagem. Erro de rede é outra coisa: propaga, e o motor tenta de
           novo com recuo. Confundir os dois faz o tablet insistir para sempre
           num apontamento que nunca vai ser aceito. */
        const m = (erro as Error).message;
        if (/HTTP 4\d\d/.test(m)) {
          respostas.push({ id: item.id, resultado: 'recusado', mensagem: m.slice(0, 240) });
        } else {
          throw erro;
        }
      }
    }

    return respostas;
  }

  /**
   * Baixa uma página de uma coleção, do que mudou desde a marca d'água.
   *
   * A paginação é por marca d'água, não por `offset`: com offset, uma linha
   * inserida no meio da varredura faz a página seguinte pular um registro. Com
   * marca d'água isso não acontece — no pior caso um registro desce duas vezes,
   * e como a escrita local é por id, descer duas vezes é inofensivo.
   */
  async baixar(colecao: NomeColecao, marca: string, limite: number): Promise<PaginaDeDescida> {
    const tabela = colecao === 'os' ? (this.o.tabelaOs ?? TABELA.os) : TABELA[colecao];
    if (!tabela) {
      return { colecao, registros: [], removidos: [], marca, fim: true };
    }

    const coluna = COLUNA_MARCA[colecao] ?? 'updated_at';
    const embute = EMBUTE_NUMERO_DA_OS[colecao] === true;
    const params = new URLSearchParams({
      select: embute ? '*,os:pcpgbu_os(numero)' : '*',
      order: `${coluna}.asc`,
      limit: String(limite),
      [coluna]: `gt.${marca}`,
    });

    const r = await this.buscar(`${this.o.url}/rest/v1/${tabela}?${params}`, {
      headers: this.cabecalhos(),
    });
    if (!r.ok) throw new Error(`baixar ${colecao}: HTTP ${r.status} ${(await r.text()).slice(0, 240)}`);

    const brutos = (await r.json()) as Array<Record<string, unknown>>;
    const registros = brutos.map((x) => desembutirNumeroDaOs(paraCamel<Record<string, unknown>>(x), embute));
    /* A marca d'água nova é o carimbo da ÚLTIMA linha da página. Vem como texto
       do PostgREST; se vier qualquer outra coisa, fica a marca anterior —
       avançar com lixo faria o tablet pular registros para sempre. */
    /* A marca d'água sai do registro BRUTO: a coluna tem o nome do servidor. */
    const ultimo = brutos.at(-1);
    const bruto = ultimo?.[coluna];
    const marcaNova = typeof bruto === 'string' ? bruto : marca;

    return {
      colecao,
      registros,
      /* O servidor não apaga: OS cancelada continua existindo com
         `cancelada_em` preenchido. Por isso não há removidos — e é de
         propósito, porque apagar é o que faz histórico sumir. */
      removidos: [],
      marca: marcaNova,
      fim: registros.length < limite,
    };
  }

  /** O pulso do aparelho, que também devolve o conjunto de trabalho do setor. */
  async conjuntoDeTrabalho(marcas: Record<string, string>): Promise<unknown> {
    if (!this.o.dispositivoId) throw new Error('Aparelho sem pareamento.');
    return this.rpc('pcpgbu_sync_baixar', {
      p_dispositivo: this.o.dispositivoId,
      p_marcas: marcas,
    });
  }
}

/** Qual função de banco atende cada tipo de item da fila. */
const FUNCAO_POR_TIPO: Record<string, string> = {
  // Nomes do tablet (produção).
  prioridade: 'pcpgbu_os_prioridade',
  cancelamento: 'pcpgbu_os_cancelar',
  expedicao: 'pcpgbu_expedicao_registrar',
  dePara: 'pcpgbu_de_para_aprender',
  // Nomes do escritório (a ação chega como o nome da RPC).
  os_prioridade: 'pcpgbu_os_prioridade',
  os_cancelar: 'pcpgbu_os_cancelar',
  os_reabrir: 'pcpgbu_os_reabrir',
  os_marcar_pronta: 'pcpgbu_os_marcar_pronta',
  expedicao_registrar: 'pcpgbu_expedicao_registrar',
  de_para_aprender: 'pcpgbu_de_para_aprender',
  etapa_adicionar: 'pcpgbu_etapa_adicionar',
  etapa_ordem: 'pcpgbu_etapa_ordem',
  etapa_limpar_marca: 'pcpgbu_etapa_limpar_marca',
  alerta_resolver: 'pcpgbu_alerta_resolver',
  importar_os_web: 'pcpgbu_importar_os_web',
  setor_salvar: 'pcpgbu_setor_salvar',
  estacao_salvar: 'pcpgbu_estacao_salvar',
  motivo_salvar: 'pcpgbu_motivo_salvar',
  pessoa_salvar: 'pcpgbu_pessoa_salvar',
  dispositivo_salvar: 'pcpgbu_dispositivo_salvar',
  de_para_salvar: 'pcpgbu_de_para_salvar',
  de_para_apagar: 'pcpgbu_de_para_apagar',
  parametros_salvar: 'pcpgbu_parametros_salvar',
  calendario_salvar: 'pcpgbu_calendario_salvar',
};
